import apiV2 from '../api/v2';
import type { RangeKey } from '../hooks/useBillingRange';
import type { DmNotificationResolution } from './dmNotifications';
import type { OverviewCategory, OverviewLevel } from './dmMonitoringOverview';
import type { VaFlowResponse } from './vaMonitoringOverview';

export type HistoryBucket = 'day' | 'week' | 'month';
export type HistoryArchiveStatus = 'included' | 'not_configured' | 'failed';

export interface HistoryResolvedCount {
    category: OverviewCategory;
    resolution: DmNotificationResolution;
    count: number;
}

export interface HistoryPatientCounts {
    notificationsRaised: number;
    notificationsResolved: number;
    messagesRaised: number;
    messagesResolved: number;
    // Distinct across both kinds, so below the sum when a patient has both.
    raisedTotal: number;
    resolvedTotal: number;
}

export interface HistorySeriesPoint {
    // YYYY-MM-DD in the requested time zone.
    bucketStart: string;
    notificationsRaised: number;
    notificationsResolved: number;
    messagesRaised: number;
    messagesResolved: number;
    // Optional for older backends.
    patients?: HistoryPatientCounts;
}

export interface MonitoringOverviewHistory {
    range: RangeKey;
    from: string | null;
    to: string;
    tz: string;
    bucket: HistoryBucket;
    archive: HistoryArchiveStatus;
    raised: {
        notifications: number;
        messages: number;
        levels: { level: OverviewLevel; count: number }[];
        eventTypes: { eventType: string; count: number }[];
    };
    resolved: HistoryResolvedCount[];
    medianDashboardResolveSeconds: Record<OverviewCategory, number | null>;
    series: HistorySeriesPoint[];
}

export const fetchMonitoringOverviewHistory = async (
    range: RangeKey,
    tz: string
): Promise<MonitoringOverviewHistory> => {
    const response = await apiV2.get<{ success: boolean; data: MonitoringOverviewHistory }>(
        '/admin/monitoring/overview/history',
        { params: { range, tz } }
    );
    return response.data.data;
};

export interface PerPersonPerson {
    userId: string;
    name: string | null;
}

export interface PerPersonBucket {
    // YYYY-MM-DD in the requested time zone.
    bucketStart: string;
    total: number;
    // Only userIds with more than zero.
    counts: Record<string, number>;
    // Distinct patients team-wide, below the sum of patientCounts when people share a patient.
    // Optional for older backends.
    patientTotal?: number;
    // Distinct patients per userId, only those with more than zero; optional for older backends.
    patientCounts?: Record<string, number>;
}

export interface PerPersonCategorySeries {
    // Window total desc.
    people: PerPersonPerson[];
    // Zero-filled, oldest first.
    series: PerPersonBucket[];
}

export interface MonitoringPerPersonResponse {
    bucket: HistoryBucket;
    from: string;
    to: string;
    archive: HistoryArchiveStatus;
    notifications: PerPersonCategorySeries;
    messages: PerPersonCategorySeries;
}

export interface ResolutionBucket {
    // YYYY-MM-DD in the requested time zone.
    bucketStart: string;
    total: number;
    counts: Record<DmNotificationResolution, number>;
}

export interface MonitoringResolutionsResponse {
    bucket: HistoryBucket;
    from: string;
    to: string;
    archive: HistoryArchiveStatus;
    // Zero-filled, oldest first.
    notifications: ResolutionBucket[];
    messages: ResolutionBucket[];
}

export interface BacklogSeriesPoint {
    // YYYY-MM-DD in the requested time zone.
    bucketStart: string;
    at: string;
    // Still open at the end of the bucket; null before coverageStartsAt.
    notifications: number | null;
    messages: number | null;
    // Distinct patients with open items; total counts a patient with both once.
    // Null with the counts, and missing from payloads cached before it existed.
    patients?: { notifications: number; messages: number; total: number } | null;
}

export interface MonitoringBacklogResponse {
    tz: string;
    bucket: HistoryBucket;
    archive: HistoryArchiveStatus;
    coverageStartsAt: string;
    // Counts for buckets before this are too low.
    reliableFrom: string;
    // Oldest first.
    series: BacklogSeriesPoint[];
}

// The trailing-window charts: each kind is also its path under /admin/monitoring/overview.
export interface MonitoringTrailingResponses {
    'per-person': MonitoringPerPersonResponse;
    resolutions: MonitoringResolutionsResponse;
    backlog: MonitoringBacklogResponse;
    'virtual-appointments/flow': VaFlowResponse;
}

export type MonitoringTrailingKind = keyof MonitoringTrailingResponses;

export const fetchMonitoringTrailing = async <K extends MonitoringTrailingKind>(
    kind: K,
    bucket: HistoryBucket,
    tz: string
): Promise<MonitoringTrailingResponses[K]> => {
    const response = await apiV2.get<{ success: boolean; data: MonitoringTrailingResponses[K] }>(
        `/admin/monitoring/overview/${kind}`,
        { params: { bucket, tz } }
    );
    return response.data.data;
};
